import { useEffect, useRef, useState } from 'react'
import { useTexte } from '../../texte'

import type { Reaction } from '../../api/types'
import { SmileyIcon } from '../../components/Icons'
import { useOnline } from '../../hooks/useOutbox'
import { useLangdruck } from './Blasenmenue'
import { Emojiwahl } from './Emojiwahl'
import { Reaktionsliste, zeigtFremdeNamen } from './Reaktionsliste'

/**
 * Was zur Auswahl steht — dieselbe Liste wie im Server.
 *
 * Eine feste Auswahl statt einer vollständigen Emoji-Tastatur: sie passt in
 * eine Zeile, ist mit dem Daumen zu treffen, und in der Datenbank landet
 * nichts, was niemand erwartet hat.
 */
export const EMOJI = ['❤️', '👍', '😍', '😂', '😮', '🎉', '👏', '🙏']

/**
 * Die Reaktionen zu einem Ziel — einer Nachricht oder einem Foto.
 *
 * Angezeigt wird, was der Server geschickt hat. Er entscheidet auch, wessen
 * Namen dabeistehen: ein Empfänger sieht die Reaktionen der anderen nicht,
 * sonst verriete ein Beitrag mit drei Herzen, wie viele mitlesen.
 */
export function Reactions({
  reactions,
  onPick,
  busy,
  compact,
  imFluss,
  nurAnzeigen,
}: {
  reactions: Reaction[]
  onPick: (emoji: string) => void
  busy?: boolean
  /** Über einem Foto: kleiner und auf dunklem Grund. */
  compact?: boolean
  /**
   * Die Auswahl steht **im Fluss** statt zu schweben.
   *
   * **Aus dem Betrieb, und es war ein selbst gebauter Fehler:** seit die
   * Auswahl ein Raster ist, ist sie 152 statt 56 Punkte hoch. Im
   * Highlight-Blatt scrollt der Inhalt in einem Kasten von 126 Punkten
   * (`.bogen__blatt`, `overflow: auto`) — nach oben aufgeklappt ragte sie
   * oben heraus, wurde dort **abgeschnitten**, und die acht Zeichen lagen
   * genau im abgeschnittenen Teil. Angetippt hat man die Landkarte
   * dahinter. Gemessen, nicht vermutet: `elementFromPoint` lieferte den
   * Leaflet-Kasten.
   *
   * Nach unten aufgeklappt lief sie stattdessen aus dem Fenster: sie passte
   * in keine Richtung. Im Fluss schiebt sie den Inhalt auseinander, der
   * Kasten wird länger, und man scrollt zu ihr — nichts wird abgeschnitten.
   * Wo ein Scrollkasten ist, weiß das Bauteil, das sie einsetzt; der Browser
   * weiß es nicht von allein.
   */
  imFluss?: boolean
  /**
   * Nur zeigen, was schon dasteht — **ohne den Knopf zum Reagieren**.
   *
   * An der Sprechblase liegt die Auswahl seit #8 im Menü beim langen
   * Drücken, wie bei iMessage und WhatsApp. Der Smiley-Knopf saß dort als
   * helle Scheibe mitten auf der blauen Blase und war das Lauteste darin;
   * die vergebenen Reaktionen bleiben, denn die sind Inhalt und kein
   * Bedienelement. Am Foto und am Highlight gibt es kein solches Menü — dort
   * bleibt der Knopf.
   */
  nurAnzeigen?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [alle, setAlle] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  // **Ohne Netz abgeschaltet, nicht stumm.** Ein Emoji ist ein Schreibzugriff
  // wie jeder andere; vorher passierte beim Antippen schlicht nichts, ohne
  // ein Wort -- die einzige Stelle der Anwendung, die geschwiegen hat.
  const online = useOnline()
  const gesperrt = busy || !online
  const t = useTexte()
  const hinweis = online ? undefined : t.allgemein.ohneNetz

  /*
   * **A long press on a given emoji shows who gave it** (#110).
   *
   * From use, asked for everywhere and the same everywhere: chat, photo in
   * the large view, highlight, spot -- this component is all four, so the
   * gesture lives here and nowhere else. A short tap keeps its meaning (set
   * or take back one's own reaction).
   *
   * Only where the server sent names beyond one's own
   * (`zeigtFremdeNamen()`); otherwise the press does what it always did.
   * Reading needs no network, so it works offline too -- unlike the tap.
   */
  const [liste, setListe] = useState(false)
  const mitNamen = zeigtFremdeNamen(reactions)
  // A long press ends in a `pointerup`, and the browser sends a `click`
  // after it. Without this mark the press that opened the list would also
  // toggle one's own reaction.
  const eben = useRef(false)
  const griffe = useLangdruck(() => {
    eben.current = true
    setListe(true)
  })
  const chipGriffe = mitNamen
    ? {
        onPointerDown: (event: React.PointerEvent) => {
          // The row around a chat bubble has its own long press (the menu);
          // the chip is the nearer target and keeps the gesture.
          event.stopPropagation()
          eben.current = false
          griffe.onPointerDown(event)
        },
        onPointerMove: griffe.onPointerMove,
        onPointerUp: griffe.onPointerUp,
        onPointerCancel: griffe.onPointerCancel,
        onContextMenu: (event: React.MouseEvent) => {
          event.stopPropagation()
          griffe.onContextMenu(event)
        },
      }
    : {}

  useEffect(() => {
    if (!open) return
    function outside(event: MouseEvent) {
      if (!box.current?.contains(event.target as Node)) {
        setOpen(false)
        setAlle(false)
      }
    }
    // Nächster Tick: der Klick, der geöffnet hat, soll nicht gleich schließen.
    const id = setTimeout(() => document.addEventListener('click', outside), 0)
    return () => {
      clearTimeout(id)
      document.removeEventListener('click', outside)
    }
  }, [open])

  return (
    <div
      ref={box}
      className={compact ? 'reactions reactions--compact' : 'reactions'}
      onClick={(event) => event.stopPropagation()}
    >
      {/*
        **Der Satz steht da, nicht nur im `title`** (Codereview E3).
        Ein Tooltip ist auf dem Telefon unsichtbar, und die Knöpfe sind dann
        grau ohne einen Grund -- ausgerechnet bei den Reaktionen, die
        `CLAUDE.md` als den einen stillen Fall nennt, den es nie wieder geben
        soll. Die Löschleiste macht es längst richtig.

        Der `title` bleibt daneben: am Rechner steht er am Zeiger, und dort
        stört er nicht.
      */}
      {hinweis !== undefined && (
        <span className="reactions__hinweis" role="status">{hinweis}</span>
      )}

      {reactions.map((reaction) => (
        <button
          key={reaction.emoji}
          type="button"
          className={reaction.mine ? 'reaction reaction--mine' : 'reaction'}
          // Not `disabled` while the list can open: a disabled button gets
          // no pointer events, and offline the names would be out of reach.
          // The tap stays off through `gesperrt` below.
          disabled={gesperrt && !mitNamen}
          aria-disabled={gesperrt || undefined}
          data-reaktion-chip={reaction.emoji}
          {...chipGriffe}
          onClick={() => {
            if (eben.current) {
              eben.current = false
              return
            }
            if (!gesperrt) onPick(reaction.emoji)
          }}
          // Die Namen kommen vom Server -- steht dort nichts, steht hier auch
          // nichts.
          title={hinweis ?? reaction.who.join(', ')}
        >
          <span aria-hidden="true">{reaction.emoji}</span>
          {reaction.count > 1 && <span className="reaction__count">{reaction.count}</span>}
          <span className="visually-hidden">
            {reaction.who.length > 0 ? reaction.who.join(', ') : `${reaction.count}`}
          </span>
        </button>
      ))}

      {!nurAnzeigen && (
      <button
        type="button"
        className="reaction reaction--add"
        disabled={gesperrt}
        onClick={() => setOpen((value) => !value)}
        aria-label={t.chat.reagierenName}
        aria-expanded={open}
        title={hinweis ?? t.chat.reagieren}
      >
        <SmileyIcon size={compact ? 14 : 15} />
      </button>
      )}

      {/* Die Auswahl hängt am Knopf: ohne ihn kann `open` nie wahr werden. */}
      {open && !alle && (
        <div className={imFluss ? 'picker picker--inline' : 'picker'} role="menu">
          {EMOJI.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className="picker__item"
              role="menuitem"
              onClick={() => {
                onPick(emoji)
                setOpen(false)
              }}
            >
              {emoji}
            </button>
          ))}

          {/* **Der Weg zu allem anderen — und er führt nicht mehr über die
              Tastatur.** Die erste Version öffnete ein Feld, damit die
              Emoji-Tastatur des Geräts aufgeht. Sie geht auch auf, aber auf
              der Buchstaben-Ebene: welche Ebene eine Tastatur zeigt, ist
              nicht zu setzen, dafür gibt es keine Schnittstelle. Aus dem
              Betrieb: „man muss manuell auf Emoji umschalten." Also eine
              eigene Auswahl, die auf jedem Gerät gleich aussieht. */}
          <button
            type="button"
            className="picker__item picker__mehr"
            role="menuitem"
            onClick={() => setAlle(true)}
            aria-label={t.chat.mehrEmoji}
            title={t.chat.mehrEmoji}
          >
            ＋
          </button>
        </div>
      )}

      {liste && <Reaktionsliste gruppen={[{ reactions }]} onZu={() => setListe(false)} />}

      {open && alle && (
        <Emojiwahl
          imFluss={imFluss}
          onPick={onPick}
          onZu={() => {
            setOpen(false)
            setAlle(false)
          }}
        />
      )}
    </div>
  )
}
